import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { getFolderFiles } from '@/user_files/files/fetch/get-folder-files'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { isSelectableProcessingStatus } from '@/user_files/utils/processing-status'
import { uniquePreserveOrder } from '@/util/array-utils'
import type { AppDispatch } from '@/store/store'
import { isAbortError } from '@/util/errors/is-abort-error'

export type FolderSelectionState = 'unchecked' | 'checked' | 'partial-selected' | 'loading' | 'error'

export type RecursiveFolderSelectionMessage = {
  variant: 'error'
  text: string
}

type FileSkipReason = 'processing' | 'unselectable'

type UseRecursiveFolderSelectionParams = {
  dispatch: AppDispatch
  includeProcessingFiles?: boolean
  filterSelectableFiles?: (file: UserFileRead) => boolean
  isFileSelectable?: (file: UserFileRead) => boolean
  selectedFileIds: string[]
  setSelectedFileIds: Dispatch<SetStateAction<string[]>>
}

type UseRecursiveFolderSelectionReturn = {
  clearRecursiveFolderSelection: () => void
  /** How many currently selected folders resolved with no files the caller's context considers. */
  emptySelectedFolderCount: number
  getFolderSelectionTooltipMessage: (folderId: string) => string | null
  getFolderSelectionState: (folderId: string) => FolderSelectionState
  hasRecursiveFolderSelectionPending: boolean
  recursiveFolderSelectionMessage: RecursiveFolderSelectionMessage | null
  toggleFolderSelection: (folder: FolderResponse) => Promise<void>
}

const folderSelectFailedMessage = 'Unable to select files in this folder. Please try again.'
const folderSkippedProcessingMessage = 'Some files are still processing'
const folderSkippedUnselectableMessage = 'Some files could not be selected'

function getSelectableFiles(
  files: UserFileRead[],
  isFileSelectable?: (file: UserFileRead) => boolean,
  filterSelectableFiles?: (file: UserFileRead) => boolean,
  includeProcessingFiles = false
): UserFileRead[] {
  return files.filter(
    (file) =>
      (filterSelectableFiles?.(file) ?? true) &&
      isSelectableProcessingStatus(file.processing_status, includeProcessingFiles) &&
      (isFileSelectable?.(file) ?? true)
  )
}

function getFileSkipReason(
  file: UserFileRead,
  isFileSelectable?: (file: UserFileRead) => boolean,
  filterSelectableFiles?: (file: UserFileRead) => boolean,
  includeProcessingFiles = false
): FileSkipReason | null {
  if (!(filterSelectableFiles?.(file) ?? true)) {
    return null
  }

  if (!isSelectableProcessingStatus(file.processing_status, includeProcessingFiles)) {
    const isProcessing = file.processing_status === UserFileProcessingStatus.Pending || file.processing_status === UserFileProcessingStatus.Processing
    return isProcessing ? 'processing' : 'unselectable'
  }

  if (!(isFileSelectable?.(file) ?? true)) {
    return 'unselectable'
  }

  return null
}

function getFolderSkippedMessage(
  files: UserFileRead[],
  isFileSelectable?: (file: UserFileRead) => boolean,
  filterSelectableFiles?: (file: UserFileRead) => boolean,
  includeProcessingFiles = false
): string | null {
  const skipReasons = files
    .map((file) => getFileSkipReason(file, isFileSelectable, filterSelectableFiles, includeProcessingFiles))
    .filter((reason): reason is FileSkipReason => Boolean(reason))

  if (skipReasons.includes('unselectable')) return folderSkippedUnselectableMessage
  if (skipReasons.includes('processing')) return folderSkippedProcessingMessage
  return null
}

export function useRecursiveFolderSelection({
  dispatch,
  includeProcessingFiles = false,
  filterSelectableFiles,
  isFileSelectable,
  selectedFileIds,
  setSelectedFileIds,
}: UseRecursiveFolderSelectionParams): UseRecursiveFolderSelectionReturn {
  const [resolvedFilesByFolderId, setResolvedFilesByFolderId] = useState<Record<string, UserFileRead[]>>({})
  const [loadingFolderIds, setLoadingFolderIds] = useState<Record<string, true>>({})
  const [errorByFolderId, setErrorByFolderId] = useState<Record<string, true>>({})
  // Folders that resolved with nothing this caller's context considers: genuinely empty, or - on a
  // provider tab - holding only files that tab filters out (wrong provider, tombstoned at the source),
  // which the tab lists as nothing and so reads as empty. Recorded when the folder resolves, because
  // whether a pick contributed anything is a property of that pick; deriving it later would re-judge
  // it under whichever filter happens to be active when the selection is applied.
  const [emptyFolderIds, setEmptyFolderIds] = useState<Record<string, true>>({})
  // Track active recursive fetches so Clear/close can cancel stale selection work.
  const requestControllersByFolderIdRef = useRef<Record<string, AbortController>>({})

  const selectedFileIdSet = useMemo(() => new Set(selectedFileIds), [selectedFileIds])
  const hasRecursiveFolderSelectionPending = Object.keys(loadingFolderIds).length > 0

  const abortRecursiveFolderSelectionRequests = useCallback(() => {
    Object.values(requestControllersByFolderIdRef.current).forEach((controller) => controller.abort())
    requestControllersByFolderIdRef.current = {}
  }, [])

  const clearRecursiveFolderSelection = useCallback(() => {
    // Clearing the selection also invalidates any in-flight folder select response.
    abortRecursiveFolderSelectionRequests()
    setResolvedFilesByFolderId({})
    setLoadingFolderIds({})
    setErrorByFolderId({})
    setEmptyFolderIds({})
  }, [abortRecursiveFolderSelectionRequests])

  useEffect(() => {
    return () => {
      abortRecursiveFolderSelectionRequests()
    }
  }, [abortRecursiveFolderSelectionRequests])

  const getFolderSelectionState = useCallback(
    (folderId: string): FolderSelectionState => {
      if (loadingFolderIds[folderId]) return 'loading'
      if (errorByFolderId[folderId] && !resolvedFilesByFolderId[folderId]) return 'error'

      const resolvedFiles = resolvedFilesByFolderId[folderId]
      if (!resolvedFiles) return 'unchecked'

      const selectableFiles = getSelectableFiles(resolvedFiles, isFileSelectable, filterSelectableFiles, includeProcessingFiles)
      const selectableIds = selectableFiles.map((file) => file.id)
      const consideredFiles = filterSelectableFiles ? resolvedFiles.filter(filterSelectableFiles) : resolvedFiles
      const hasSkippedFiles = consideredFiles.length > selectableFiles.length

      if (selectableIds.length === 0) {
        return hasSkippedFiles ? 'partial-selected' : 'checked'
      }

      const selectedSelectableCount = selectableIds.filter((fileId) => selectedFileIdSet.has(fileId)).length
      if (selectedSelectableCount === 0) return 'unchecked'
      if (selectedSelectableCount < selectableIds.length) return 'partial-selected'
      if (hasSkippedFiles) return 'partial-selected'
      return 'checked'
    },
    [errorByFolderId, filterSelectableFiles, isFileSelectable, includeProcessingFiles, loadingFolderIds, resolvedFilesByFolderId, selectedFileIdSet]
  )

  const getFolderSelectionTooltipMessage = useCallback(
    (folderId: string): string | null => {
      const resolvedFiles = resolvedFilesByFolderId[folderId]
      if (!resolvedFiles || getFolderSelectionState(folderId) !== 'partial-selected') return null
      return getFolderSkippedMessage(resolvedFiles, isFileSelectable, filterSelectableFiles, includeProcessingFiles)
    },
    [filterSelectableFiles, getFolderSelectionState, isFileSelectable, includeProcessingFiles, resolvedFilesByFolderId]
  )

  // Pruned alongside the resolved files, so the count tracks exactly which empty folders are still ticked.
  const emptySelectedFolderCount = Object.keys(emptyFolderIds).length

  // Read the failure off the per-folder map, not a shared string that any later success cleared: two
  // folders can resolve at once, so a folder's failure must survive an unrelated folder settling.
  const hasFolderSelectFailure = Object.keys(errorByFolderId).length > 0

  const recursiveFolderSelectionMessage = useMemo(
    (): RecursiveFolderSelectionMessage | null => (hasFolderSelectFailure ? { variant: 'error', text: folderSelectFailedMessage } : null),
    [hasFolderSelectFailure]
  )

  const toggleFolderSelection = useCallback(
    async (folder: FolderResponse) => {
      const folderId = folder.id
      if (loadingFolderIds[folderId]) return

      const resolvedFiles = resolvedFilesByFolderId[folderId]
      if (resolvedFiles) {
        const selectableIds = getSelectableFiles(resolvedFiles, isFileSelectable, filterSelectableFiles, includeProcessingFiles).map((file) => file.id)
        const selectedSet = new Set(selectedFileIds)
        const allSelectableSelected = selectableIds.length === 0 || selectableIds.every((fileId) => selectedSet.has(fileId))

        if (allSelectableSelected) {
          const idsToRemove = new Set(selectableIds)
          setSelectedFileIds((prev) => prev.filter((fileId) => !idsToRemove.has(fileId)))
          setResolvedFilesByFolderId((prev) => {
            const { [folderId]: _removed, ...next } = prev
            return next
          })
          setEmptyFolderIds((prev) => {
            const { [folderId]: _removed, ...next } = prev
            return next
          })
          return
        }

        setSelectedFileIds((prev) => uniquePreserveOrder([...prev, ...selectableIds]))
        return
      }

      setLoadingFolderIds((prev) => ({ ...prev, [folderId]: true }))
      setErrorByFolderId((prev) => {
        const { [folderId]: _removed, ...next } = prev
        return next
      })

      // Store the controller before awaiting so later clears can abort this exact request.
      const requestController = new AbortController()
      requestControllersByFolderIdRef.current[folderId] = requestController

      try {
        const response = await getFolderFiles(folderId, {
          recursive: true,
          signal: requestController.signal,
          statuses: [UserFileProcessingStatus.Processing, UserFileProcessingStatus.Completed, UserFileProcessingStatus.Failed],
        })

        // A stale response must not re-add files after Clear, close, or a newer request.
        if (requestController.signal.aborted || requestControllersByFolderIdRef.current[folderId] !== requestController) return

        dispatch(UserFilesActions.upsertUserFiles(response.files))

        const selectableIds = getSelectableFiles(response.files, isFileSelectable, filterSelectableFiles, includeProcessingFiles).map((file) => file.id)
        const consideredFiles = response.files.filter((file) => filterSelectableFiles?.(file) ?? true)
        setResolvedFilesByFolderId((prev) => ({ ...prev, [folderId]: response.files }))
        if (consideredFiles.length === 0) {
          setEmptyFolderIds((prev) => ({ ...prev, [folderId]: true }))
        }
        setSelectedFileIds((prev) => uniquePreserveOrder([...prev, ...selectableIds]))
      } catch (error) {
        if (requestController.signal.aborted || isAbortError(error) || requestControllersByFolderIdRef.current[folderId] !== requestController) return

        setErrorByFolderId((prev) => ({ ...prev, [folderId]: true }))
      } finally {
        // Only the active request for this folder should clear its loading state.
        if (requestControllersByFolderIdRef.current[folderId] === requestController) {
          delete requestControllersByFolderIdRef.current[folderId]
          setLoadingFolderIds((prev) => {
            const { [folderId]: _removed, ...next } = prev
            return next
          })
        }
      }
    },
    [dispatch, filterSelectableFiles, isFileSelectable, includeProcessingFiles, loadingFolderIds, resolvedFilesByFolderId, selectedFileIds, setSelectedFileIds]
  )

  return {
    clearRecursiveFolderSelection,
    emptySelectedFolderCount,
    getFolderSelectionTooltipMessage,
    getFolderSelectionState,
    hasRecursiveFolderSelectionPending,
    recursiveFolderSelectionMessage,
    toggleFolderSelection,
  }
}
